/**
 * Google Analytics (gtag.js) Event Tracking Utility
 * Measurement ID: G-JM3QNT75HG (configured in index.html)
 *
 * This module provides a centralized interface for all GA4 custom events.
 * All event names follow GA4 recommended naming conventions (snake_case).
 */

// Helper: safely call gtag
const gtag = (...args) => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag(...args);
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE VIEWS (SPA virtual page views)
// ─────────────────────────────────────────────────────────────────────────────

export const trackPageView = (pagePath, pageTitle) => {
    gtag('config', 'G-JM3QNT75HG', {
        page_path: pagePath,
        page_title: pageTitle,
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// LANDING PAGE EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** User views the landing page */
export const trackLandingPageView = () => {
    gtag('event', 'landing_page_view', {
        event_category: 'landing',
        event_label: 'landing_page',
    });
};

/** Hero CTA clicked: "Find Your API's Breaking Point" */
export const trackHeroCtaClick = () => {
    gtag('event', 'hero_cta_click', {
        event_category: 'landing',
        event_label: 'find_breaking_point',
    });
};

/** Header "Start Test" button clicked */
export const trackStartTestClick = (location = 'header') => {
    gtag('event', 'start_test_click', {
        event_category: 'landing',
        event_label: location, // 'header', 'mobile_nav'
    });
};

/** Navigation link clicked on landing page */
export const trackLandingNavClick = (section) => {
    gtag('event', 'landing_nav_click', {
        event_category: 'landing',
        event_label: section, // 'how_it_works', 'pricing', 'blog'
    });
};

/** "How It Works" section viewed (scrolled into view) */
export const trackHowItWorksView = () => {
    gtag('event', 'how_it_works_view', {
        event_category: 'landing',
        event_label: 'how_it_works_section',
    });
};

/** Use Cases carousel section viewed */
export const trackUseCasesSectionView = () => {
    gtag('event', 'use_cases_section_view', {
        event_category: 'landing',
        event_label: 'use_cases_carousel',
    });
};

/** Load Profiles section viewed */
export const trackLoadProfilesSectionView = () => {
    gtag('event', 'load_profiles_section_view', {
        event_category: 'landing',
        event_label: 'advanced_load_profiles',
    });
};

/** Pricing section viewed */
export const trackPricingSectionView = () => {
    gtag('event', 'pricing_section_view', {
        event_category: 'landing',
        event_label: 'pricing',
    });
};

/** Comparison section viewed ("Why Teams Switch") */
export const trackComparisonSectionView = () => {
    gtag('event', 'comparison_section_view', {
        event_category: 'landing',
        event_label: 'why_teams_switch',
    });
};

/** Final CTA section viewed */
export const trackCtaSectionView = () => {
    gtag('event', 'cta_section_view', {
        event_category: 'landing',
        event_label: 'final_cta',
    });
};

/** Final CTA button clicked: "Run Your First Test Now" */
export const trackFinalCtaClick = () => {
    gtag('event', 'final_cta_click', {
        event_category: 'landing',
        event_label: 'run_first_test_now',
    });
};

/** Footer link clicked */
export const trackFooterLinkClick = (linkName) => {
    gtag('event', 'footer_link_click', {
        event_category: 'navigation',
        event_label: linkName, // 'how_it_works', 'pricing', 'blog', 'privacy_policy', 'contact'
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// PRICING & CHECKOUT EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** User clicks "Buy Now" on a pricing card (landing page) */
export const trackPricingBuyClick = (tierId, tierName, price, credits, location = 'landing') => {
    gtag('event', 'pricing_buy_click', {
        event_category: 'pricing',
        event_label: tierId,
        tier_name: tierName,
        price: price,
        credits: credits,
        location: location, // 'landing' or 'app_modal'
    });

    // Also fire GA4 recommended ecommerce event
    gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: price,
        items: [{
            item_id: tierId,
            item_name: tierName,
            price: price,
            quantity: 1,
            item_category: 'credit_pack',
        }],
    });
};

/** Checkout opened (overlay appears) */
export const trackCheckoutOpened = (tierId, tierName, price) => {
    gtag('event', 'checkout_opened', {
        event_category: 'checkout',
        event_label: tierId,
        tier_name: tierName,
        price: price,
    });
};

/** Checkout completed successfully */
export const trackCheckoutSuccess = (tierId, tierName, price) => {
    gtag('event', 'purchase', {
        transaction_id: `txn_${Date.now()}`,
        currency: 'USD',
        value: price,
        items: [{
            item_id: tierId,
            item_name: tierName,
            price: price,
            quantity: 1,
            item_category: 'credit_pack',
        }],
    });

    gtag('event', 'checkout_success', {
        event_category: 'checkout',
        event_label: tierId,
        tier_name: tierName,
        price: price,
    });
};

/** Checkout error */
export const trackCheckoutError = (tierId, errorMessage) => {
    gtag('event', 'checkout_error', {
        event_category: 'checkout',
        event_label: tierId,
        error_message: errorMessage,
    });
};

/** Pricing modal opened (in-app) */
export const trackPricingModalOpened = (location = 'header') => {
    gtag('event', 'pricing_modal_opened', {
        event_category: 'pricing',
        event_label: location, // 'header', 'new_test_upgrade'
    });
};

/** Pricing modal closed */
export const trackPricingModalClosed = () => {
    gtag('event', 'pricing_modal_closed', {
        event_category: 'pricing',
        event_label: 'modal_closed',
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTHENTICATION EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Login page viewed */
export const trackLoginPageView = () => {
    gtag('event', 'login_page_view', {
        event_category: 'auth',
        event_label: 'login',
    });
};

/** OTP send button clicked */
export const trackOtpSendClick = () => {
    gtag('event', 'otp_send_click', {
        event_category: 'auth',
        event_label: 'send_otp',
    });
};

/** OTP sent successfully */
export const trackOtpSentSuccess = () => {
    gtag('event', 'otp_sent_success', {
        event_category: 'auth',
        event_label: 'otp_sent',
    });
};

/** OTP verification page viewed */
export const trackOtpVerificationView = () => {
    gtag('event', 'otp_verification_view', {
        event_category: 'auth',
        event_label: 'otp_verification',
    });
};

/** OTP verify button clicked */
export const trackOtpVerifyClick = () => {
    gtag('event', 'otp_verify_click', {
        event_category: 'auth',
        event_label: 'verify_otp',
    });
};

/** Login successful */
export const trackLoginSuccess = () => {
    gtag('event', 'login', {
        method: 'email_otp',
    });

    gtag('event', 'login_success', {
        event_category: 'auth',
        event_label: 'login_complete',
    });
};

/** Login failed (OTP error, etc.) */
export const trackLoginError = (errorType) => {
    gtag('event', 'login_error', {
        event_category: 'auth',
        event_label: errorType, // 'otp_send_failed', 'otp_verify_failed'
    });
};

/** User logged out */
export const trackLogout = () => {
    gtag('event', 'logout', {
        event_category: 'auth',
        event_label: 'user_logout',
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Dashboard page viewed */
export const trackDashboardView = () => {
    gtag('event', 'dashboard_view', {
        event_category: 'app',
        event_label: 'dashboard',
    });
};

/** "New Project" button clicked */
export const trackNewProjectClick = () => {
    gtag('event', 'new_project_click', {
        event_category: 'project',
        event_label: 'create_project_modal',
    });
};

/** Project created successfully */
export const trackProjectCreated = (projectName) => {
    gtag('event', 'project_created', {
        event_category: 'project',
        event_label: projectName,
    });
};

/** Project card clicked (navigate to project) */
export const trackProjectCardClick = (projectId) => {
    gtag('event', 'project_card_click', {
        event_category: 'project',
        event_label: projectId,
    });
};

/** "Buy More" credits button clicked (header) */
export const trackBuyCreditsClick = (location = 'header') => {
    gtag('event', 'buy_credits_click', {
        event_category: 'pricing',
        event_label: location, // 'header', 'mobile_menu'
    });
};

/** Credits refreshed */
export const trackCreditsRefreshed = () => {
    gtag('event', 'credits_refreshed', {
        event_category: 'app',
        event_label: 'refresh_credits',
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT PAGE EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Project page viewed */
export const trackProjectPageView = (projectId) => {
    gtag('event', 'project_page_view', {
        event_category: 'project',
        event_label: projectId,
    });
};

/** "New Test" button clicked on project page */
export const trackNewTestClick = (projectId) => {
    gtag('event', 'new_test_click', {
        event_category: 'test',
        event_label: projectId,
    });
};

/** Endpoint clicked on project page */
export const trackEndpointClick = (endpointId) => {
    gtag('event', 'endpoint_click', {
        event_category: 'project',
        event_label: endpointId,
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// NEW TEST CONFIGURATION EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** New test page viewed */
export const trackNewTestPageView = (projectId) => {
    gtag('event', 'new_test_page_view', {
        event_category: 'test',
        event_label: projectId,
    });
};

/** cURL imported */
export const trackCurlImport = () => {
    gtag('event', 'curl_import', {
        event_category: 'test',
        event_label: 'curl_imported',
    });
};

/** Postman collection imported */
export const trackPostmanImport = () => {
    gtag('event', 'postman_import', {
        event_category: 'test',
        event_label: 'postman_imported',
    });
};

/** Load profile selected */
export const trackLoadProfileSelected = (profileId) => {
    gtag('event', 'load_profile_selected', {
        event_category: 'test',
        event_label: profileId, // 'fixed', 'rampup', 'spike', 'peak'
    });
};

/** HTTP method changed */
export const trackHttpMethodChanged = (method) => {
    gtag('event', 'http_method_changed', {
        event_category: 'test',
        event_label: method,
    });
};

/** Authentication type changed */
export const trackAuthTypeChanged = (authType) => {
    gtag('event', 'auth_type_changed', {
        event_category: 'test',
        event_label: authType, // 'none', 'bearer', 'header'
    });
};

/** Test submitted (Run Test clicked) */
export const trackTestSubmitted = (config) => {
    gtag('event', 'test_submitted', {
        event_category: 'test',
        event_label: 'test_started',
        method: config.method,
        load_profile: config.loadProfile,
        connections: config.connections,
        duration: config.duration,
    });
};

/** Test failed to start */
export const trackTestStartError = (errorMessage) => {
    gtag('event', 'test_start_error', {
        event_category: 'test',
        event_label: errorMessage,
    });
};

/** Domain verification popup shown */
export const trackDomainVerificationShown = (domain) => {
    gtag('event', 'domain_verification_shown', {
        event_category: 'test',
        event_label: domain,
    });
};

/** Domain verification attempted */
export const trackDomainVerificationAttempt = (domain) => {
    gtag('event', 'domain_verification_attempt', {
        event_category: 'test',
        event_label: domain,
    });
};

/** Domain verified successfully */
export const trackDomainVerified = (domain) => {
    gtag('event', 'domain_verified', {
        event_category: 'test',
        event_label: domain,
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEST PROGRESS & RESULTS EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Test running / progress page viewed */
export const trackTestProgressView = () => {
    gtag('event', 'test_progress_view', {
        event_category: 'test',
        event_label: 'test_running',
    });
};

/** Test completed - results viewed */
export const trackTestResultsView = (testId) => {
    gtag('event', 'test_results_view', {
        event_category: 'test_results',
        event_label: testId || 'results',
    });
};

/** Test results with key metrics */
export const trackTestCompleted = (results) => {
    gtag('event', 'test_completed', {
        event_category: 'test_results',
        event_label: 'test_complete',
        total_requests: results.totalRequests,
        requests_per_second: results.requestsPerSecond,
        avg_response_time: Math.round(results.averageResponseTime || 0),
        error_rate: parseFloat((results.errorPercentage || results.errorRate || 0).toFixed(2)),
        load_profile: results.loadProfile || 'fixed',
        test_duration: results.testDuration,
    });
};

/** "Run Another Test" clicked */
export const trackRunAnotherTestClick = () => {
    gtag('event', 'run_another_test_click', {
        event_category: 'test_results',
        event_label: 'run_another',
    });
};

/** "Back to Project" clicked from results */
export const trackBackToProjectClick = () => {
    gtag('event', 'back_to_project_click', {
        event_category: 'test_results',
        event_label: 'back_to_project',
    });
};

/** Export results as CSV */
export const trackExportCsv = () => {
    gtag('event', 'export_csv', {
        event_category: 'test_results',
        event_label: 'csv_export',
    });
};

/** Export results as JSON */
export const trackExportJson = () => {
    gtag('event', 'export_json', {
        event_category: 'test_results',
        event_label: 'json_export',
    });
};

/** Share results */
export const trackShareResults = () => {
    gtag('event', 'share', {
        method: 'results_share',
        content_type: 'test_results',
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEST DETAILS / HISTORY EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Test details page viewed */
export const trackTestDetailsPageView = (endpointId) => {
    gtag('event', 'test_details_page_view', {
        event_category: 'test_details',
        event_label: endpointId,
    });
};

/** Test history item clicked */
export const trackTestHistoryClick = (testId) => {
    gtag('event', 'test_history_click', {
        event_category: 'test_details',
        event_label: testId,
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// BLOG EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Blog list page viewed */
export const trackBlogListView = () => {
    gtag('event', 'blog_list_view', {
        event_category: 'blog',
        event_label: 'blog_list',
    });
};

/** Blog post viewed */
export const trackBlogPostView = (slug) => {
    gtag('event', 'blog_post_view', {
        event_category: 'blog',
        event_label: slug,
    });
};

// ─────────────────────────────────────────────────────────────────────────────
// UPGRADE EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Insufficient credits - shows warning */
export const trackInsufficientCredits = (currentCredits) => {
    gtag('event', 'insufficient_credits', {
        event_category: 'pricing',
        event_label: 'no_credits',
        current_credits: currentCredits,
    });
};
